// components/navigation/IntegratedSearchNavBar.js
// One top-bar layout at every width: country/region pill, inline level switcher
// (or feed filters), Donate + menu on the right. Search lives in BottomNavbar
// on both desktop and mobile now, so the top bar carries no search input.
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Dropdown, Navbar, Button, Image, Modal, Spinner, Card } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { HeartFill, Newspaper, Building, FileText, List, InfoCircle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { getStateInfo } from '../../utils/feedsConfig';
import { getActiveCountries } from '../../utils/countryConfig';
import MobileNavigation from './MobileNavigation';
import ReactMarkdown from 'react-markdown';
import openCapacitorSite from '../../utils/browserUtils';
import FeedFilterBar from './FeedFilterBar';
import './FeedFilterBar.css';

// History-entry marker for navigations that come from the Federal / State /
// Province / Kingdom / Local tabs. Those tabs are peer navigation rather than a
// drill-in, so MobileBackButton reads this off location.state and hides itself
// on the entries they create. Keep the key in sync with MobileBackButton.js.
const LEVEL_SWITCH_NAV_STATE = { state: { fromLevelSwitcher: true } };

function cleanAISuggestions(content) {
  if (!content) return content;
  let cleaned = content;
  const preamblePatterns = [
    /^Here (?:are|is) (?:some|a|the)[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^Below (?:are|is)[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^I found[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^Based on[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^The following[\s\S]*?(?::\n|:\s*\n\n)/i,
    /^For official[\s\S]*?(?:\.\[\d+\])+\s*/i,
    /^For (?:local|city|government|federal|state)[\s\S]*?(?:\.\[\d+\])+\s*/i,
  ];
  const suggestionPatterns = [
    /If you(?:'d like| want),? (?:I can|you can|let me know)[\s\S]*$/i,
    /Would you like me to[\s\S]*$/i,
    /Let me know if you(?:'d like| want)[\s\S]*$/i,
    /I can (?:also |further )?(?:focus|narrow|filter|provide|help|drill)[\s\S]*$/i,
    /Do you want me to[\s\S]*$/i,
    /Want me to[\s\S]*$/i,
    /If you need[\s\S]*$/i,
    /If there's a specific[\s\S]*$/i,
    /If there is a specific[\s\S]*$/i,
    /You can also[\s\S]*$/i,
    /For (?:more|additional|further) (?:details|information|updates)[\s\S]*$/i,
    /If you(?:'d like| want| need) (?:the latest|more|to|me to)[\s\S]*$/i,
  ];
  for (const pattern of preamblePatterns) { cleaned = cleaned.replace(pattern, '').trim(); }
  for (const pattern of suggestionPatterns) { cleaned = cleaned.replace(pattern, '').trim(); }
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
  return cleaned;
}

const processCitationsInContent = (content, citations) => {
  if (!content || !citations || citations.length === 0) return content;
  let processed = content;
  citations.forEach((citation, index) => {
    const citationNumber = index + 1;
    const pattern = new RegExp(`\\[${citationNumber}\\]`, 'g');
    if (citation.url) {
      processed = processed.replace(pattern, `[[${citationNumber}]](${citation.url})`);
    }
  });
  return processed;
};

const MarkdownLink = ({ href, children }) => {
  const handleClick = (e) => { e.preventDefault(); e.stopPropagation(); openCapacitorSite(href, '_blank'); };
  return <a href={href} onClick={handleClick} style={{ cursor: 'pointer' }}>{children}</a>;
};

const ReportContent = ({ content, citations, className = '' }) => {
  const processedContent = processCitationsInContent(content, citations);
  return (
    <div className={`report-markdown-content ${className}`}>
      <ReactMarkdown components={{ a: MarkdownLink }}>{processedContent}</ReactMarkdown>
    </div>
  );
};

const US_STATES_DATA = [
  { name: "Alabama", flag: "/alabamaFlag.png", path: "/us/alabama", key: "alabama" },
  { name: "Alaska", flag: "/alaskaFlag.png", path: "/us/alaska", key: "alaska" },
  { name: "Arizona", flag: "/arizonaFlag.png", path: "/us/arizona", key: "arizona" },
  { name: "Arkansas", flag: "/arkansasFlag.png", path: "/us/arkansas", key: "arkansas" },
  { name: "California", flag: "/californiaFlag.png", path: "/us/california", key: "california" },
  { name: "Colorado", flag: "/coloradoFlag.png", path: "/us/colorado", key: "colorado" },
  { name: "Connecticut", flag: "/connecticutFlag.png", path: "/us/connecticut", key: "connecticut" },
  { name: "Delaware", flag: "/delawareFlag.png", path: "/us/delaware", key: "delaware" },
  { name: "Florida", flag: "/floridaFlag.png", path: "/us/florida", key: "florida" },
  { name: "Georgia", flag: "/georgiaFlag.png", path: "/us/georgia", key: "georgia" },
  { name: "Hawaii", flag: "/hawaiiFlag.png", path: "/us/hawaii", key: "hawaii" },
  { name: "Idaho", flag: "/idahoFlag.png", path: "/us/idaho", key: "idaho" },
  { name: "Illinois", flag: "/illinoisFlag.png", path: "/us/illinois", key: "illinois" },
  { name: "Indiana", flag: "/indianaFlag.png", path: "/us/indiana", key: "indiana" },
  { name: "Iowa", flag: "/iowaFlag.png", path: "/us/iowa", key: "iowa" },
  { name: "Kansas", flag: "/kansasFlag.png", path: "/us/kansas", key: "kansas" },
  { name: "Kentucky", flag: "/kentuckyFlag.png", path: "/us/kentucky", key: "kentucky" },
  { name: "Louisiana", flag: "/louisianaFlag.png", path: "/us/louisiana", key: "louisiana" },
  { name: "Maine", flag: "/maineFlag.png", path: "/us/maine", key: "maine" },
  { name: "Maryland", flag: "/marylandFlag.png", path: "/us/maryland", key: "maryland" },
  { name: "Massachusetts", flag: "/massachusettsFlag.png", path: "/us/massachusetts", key: "massachusetts" },
  { name: "Michigan", flag: "/michiganFlag.png", path: "/us/michigan", key: "michigan" },
  { name: "Minnesota", flag: "/minnesotaFlag.png", path: "/us/minnesota", key: "minnesota" },
  { name: "Mississippi", flag: "/mississippiFlag.png", path: "/us/mississippi", key: "mississippi" },
  { name: "Missouri", flag: "/missouriFlag.png", path: "/us/missouri", key: "missouri" },
  { name: "Montana", flag: "/montanaFlag.png", path: "/us/montana", key: "montana" },
  { name: "Nebraska", flag: "/nebraskaFlag.png", path: "/us/nebraska", key: "nebraska" },
  { name: "Nevada", flag: "/nevadaFlag.png", path: "/us/nevada", key: "nevada" },
  { name: "New Hampshire", flag: "/newHampshireFlag.png", path: "/us/newhampshire", key: "newhampshire" },
  { name: "New Jersey", flag: "/newJerseyFlag.png", path: "/us/newjersey", key: "newjersey" },
  { name: "New Mexico", flag: "/newMexicoFlag.png", path: "/us/newmexico", key: "newmexico" },
  { name: "New York", flag: "/newYorkFlag.png", path: "/us/newyork", key: "newyork" },
  { name: "North Carolina", flag: "/northCarolinaFlag.png", path: "/us/northcarolina", key: "northcarolina" },
  { name: "North Dakota", flag: "/northDakotaFlag.png", path: "/us/northdakota", key: "northdakota" },
  { name: "Ohio", flag: "/ohioFlag.png", path: "/us/ohio", key: "ohio" },
  { name: "Oklahoma", flag: "/oklahomaFlag.png", path: "/us/oklahoma", key: "oklahoma" },
  { name: "Oregon", flag: "/oregonFlag.png", path: "/us/oregon", key: "oregon" },
  { name: "Pennsylvania", flag: "/pennsylvaniaFlag.png", path: "/us/pennsylvania", key: "pennsylvania" },
  { name: "Rhode Island", flag: "/rhodeIslandFlag.png", path: "/us/rhodeisland", key: "rhodeisland" },
  { name: "South Carolina", flag: "/southCarolinaFlag.png", path: "/us/southcarolina", key: "southcarolina" },
  { name: "South Dakota", flag: "/southDakotaFlag.png", path: "/us/southdakota", key: "southdakota" },
  { name: "Tennessee", flag: "/tennesseeFlag.png", path: "/us/tennessee", key: "tennessee" },
  { name: "Texas", flag: "/texasFlag.png", path: "/us/texas", key: "texas" },
  { name: "Utah", flag: "/utahFlag.png", path: "/us/utah", key: "utah" },
  { name: "Vermont", flag: "/vermontFlag.png", path: "/us/vermont", key: "vermont" },
  { name: "Virginia", flag: "/virginiaFlag.png", path: "/us/virginia", key: "virginia" },
  { name: "Washington", flag: "/washingtonFlag.png", path: "/us/washington", key: "washington" },
  { name: "West Virginia", flag: "/westVirginiaFlag.png", path: "/us/westvirginia", key: "westvirginia" },
  { name: "Wisconsin", flag: "/wisconsinFlag.png", path: "/us/wisconsin", key: "wisconsin" },
  { name: "Wyoming", flag: "/wyomingFlag.png", path: "/us/wyoming", key: "wyoming" }
];

const CANADA_PROVINCES_DATA = [
  { name: "Alberta", flag: "/albertaFlag.png", path: "/canada/alberta", key: "alberta" },
  { name: "British Columbia", flag: "/britishColumbiaFlag.png", path: "/canada/british_columbia", key: "british_columbia" },
  { name: "Manitoba", flag: "/manitobaFlag.png", path: "/canada/manitoba", key: "manitoba" },
  { name: "New Brunswick", flag: "/newBrunswickFlag.png", path: "/canada/new_brunswick", key: "new_brunswick" },
  { name: "Newfoundland & Labrador", flag: "/newfoundlandAndLabradorFlag.png", path: "/canada/newfoundland_labrador", key: "newfoundland_labrador" },
  { name: "Northwest Territories", flag: "/northwestTerritoriesFlag.png", path: "/canada/northwest_territories", key: "northwest_territories" },
  { name: "Nova Scotia", flag: "/novaScotiaFlag.png", path: "/canada/nova_scotia", key: "nova_scotia" },
  { name: "Nunavut", flag: "/nunavutFlag.png", path: "/canada/nunavut", key: "nunavut" },
  { name: "Ontario", flag: "/ontarioFlag.png", path: "/canada/ontario", key: "ontario" },
  { name: "Prince Edward Island", flag: "/princeEdwardIslandFlag.png", path: "/canada/prince_edward_island", key: "prince_edward_island" },
  { name: "Quebec", flag: "/quebecFlag.png", path: "/canada/quebec", key: "quebec" },
  { name: "Saskatchewan", flag: "/saskatchewanFlag.png", path: "/canada/saskatchewan", key: "saskatchewan" },
  { name: "Yukon", flag: "/yukonFlag.png", path: "/canada/yukon", key: "yukon" }
];

const MEXICO_STATES_DATA = [
  { name: "Aguascalientes", flag: "/aguascalientesFlag.png", path: "/mexico/aguascalientes", key: "aguascalientes" },
  { name: "Baja California", flag: "/bajaCaliforniaFlag.png", path: "/mexico/bajacalifornia", key: "bajacalifornia" },
  { name: "Baja California Sur", flag: "/bajaCaliforniaSurFlag.png", path: "/mexico/bajacaliforniasur", key: "bajacaliforniasur" },
  { name: "Campeche", flag: "/campecheFlag.png", path: "/mexico/campeche", key: "campeche" },
  { name: "Chiapas", flag: "/chiapasFlag.png", path: "/mexico/chiapas", key: "chiapas" },
  { name: "Chihuahua", flag: "/chihuahuaFlag.png", path: "/mexico/chihuahua", key: "chihuahua" },
  { name: "Coahuila", flag: "/coahuilaFlag.png", path: "/mexico/coahuila", key: "coahuila" },
  { name: "Colima", flag: "/colimaFlag.png", path: "/mexico/colima", key: "colima" },
  { name: "Durango", flag: "/durangoFlag.png", path: "/mexico/durango", key: "durango" },
  { name: "Guanajuato", flag: "/guanajuatoFlag.png", path: "/mexico/guanajuato", key: "guanajuato" },
  { name: "Guerrero", flag: "/guerreroFlag.png", path: "/mexico/guerrero", key: "guerrero" },
  { name: "Hidalgo", flag: "/hidalgoFlag.png", path: "/mexico/hidalgo", key: "hidalgo" },
  { name: "Jalisco", flag: "/jaliscoFlag.png", path: "/mexico/jalisco", key: "jalisco" },
  { name: "Ciudad de México", flag: "/mexicoCityFlag.png", path: "/mexico/mexicocity", key: "mexicocity" },
  { name: "Estado de México", flag: "/mexicoStateFlag.png", path: "/mexico/mexicostate", key: "mexicostate" },
  { name: "Michoacán", flag: "/michoacanFlag.png", path: "/mexico/michoacan", key: "michoacan" },
  { name: "Morelos", flag: "/morelosFlag.png", path: "/mexico/morelos", key: "morelos" },
  { name: "Nayarit", flag: "/nayaritFlag.png", path: "/mexico/nayarit", key: "nayarit" },
  { name: "Nuevo León", flag: "/nuevoLeonFlag.png", path: "/mexico/nuevoleon", key: "nuevoleon" },
  { name: "Oaxaca", flag: "/oaxacaFlag.png", path: "/mexico/oaxaca", key: "oaxaca" },
  { name: "Puebla", flag: "/pueblaFlag.png", path: "/mexico/puebla", key: "puebla" },
  { name: "Querétaro", flag: "/queretaroFlag.png", path: "/mexico/queretaro", key: "queretaro" },
  { name: "Quintana Roo", flag: "/quintanaRooFlag.png", path: "/mexico/quintanaroo", key: "quintanaroo" },
  { name: "San Luis Potosí", flag: "/sanLuisPotosiFlag.png", path: "/mexico/sanluispotosi", key: "sanluispotosi" },
  { name: "Sinaloa", flag: "/sinaloaFlag.png", path: "/mexico/sinaloa", key: "sinaloa" },
  { name: "Sonora", flag: "/sonoraFlag.png", path: "/mexico/sonora", key: "sonora" },
  { name: "Tabasco", flag: "/tabascoFlag.png", path: "/mexico/tabasco", key: "tabasco" },
  { name: "Tamaulipas", flag: "/tamaulipasFlag.png", path: "/mexico/tamaulipas", key: "tamaulipas" },
  { name: "Tlaxcala", flag: "/tlaxcalaFlag.png", path: "/mexico/tlaxcala", key: "tlaxcala" },
  { name: "Veracruz", flag: "/veracruzFlag.png", path: "/mexico/veracruz", key: "veracruz" },
  { name: "Yucatán", flag: "/yucatanFlag.png", path: "/mexico/yucatan", key: "yucatan" },
  { name: "Zacatecas", flag: "/zacatecasFlag.png", path: "/mexico/zacatecas", key: "zacatecas" }
];

const UK_KINGDOMS_DATA = [
  { name: "England", flag: "/englandFlag.png", path: "/uk/england", key: "england" },
  { name: "Scotland", flag: "/scotlandFlag.png", path: "/uk/scotland", key: "scotland" },
  { name: "Wales", flag: "/walesFlag.png", path: "/uk/wales", key: "wales" },
  { name: "Northern Ireland", flag: "/northernIrelandFlag.png", path: "/uk/northernireland", key: "northernireland" }
];

// Countries that don't (yet) have sub-national region data. They get a
// Federal-only level switcher: the Federal tab is shown active, and the
// secondary level is rendered disabled (not selectable). When a country gains
// region data, give it a dedicated *_DATA set and switcher branch like the
// core four instead of relying on this generic fallback.
const NEW_COUNTRY_SLUGS = [
  'australia', 'germany', 'france', 'italy', 'israel',
  'ukraine', 'china', 'japan', 'nigeria', 'south-africa'
];

// Country slugs that appear as the first path segment ("/us/...",
// "/canada/ontario", "/japan"). Kept in sync with the switcher branches above
// plus NEW_COUNTRY_SLUGS -- if a country is added to the app's routes, add its
// slug here too or the bar will fall back to the stored country on its pages.
const COUNTRY_PATH_SLUGS = new Set([
  'us', 'canada', 'mexico', 'uk', 'all',
  ...NEW_COUNTRY_SLUGS,
]);

// The URL is the most reliable statement of which country the user is looking
// at, so it wins over both the currentCountry prop and localStorage. Without
// this, a US route could not assert itself: getEffectiveCountry ignores a
// currentCountry of 'us' (indistinguishable from the prop default), so pages
// like /us/representatives fell through to localStorage.selectedCountry and
// rendered whatever country was picked from the dropdown last.
//
// Returns null for non-country routes (/search, /feed, /settings, /about),
// which keep the previous stored-country behavior.
const detectCountryFromPath = (path) => {
  const segment = (path || '').split('/')[1];
  if (!segment) return null;
  const slug = segment.toLowerCase();
  return COUNTRY_PATH_SLUGS.has(slug) ? slug : null;
};

// The Preferences page (Location / Language / Appearance / Font) is mounted at
// /preferences in App.js. NOT the bare /settings route, which is the
// notification settings page. /settings/location is the page's former path and
// is still matched so an old link, a bookmark or a stale native build lands in
// the same state rather than on a bar full of explore controls; drop that
// alternative once the route is gone for good.
//
// This one predicate decides two things at once, which is why a stale path here
// shows up as two bugs: the bar renders its centered title instead of the
// explore controls (the page suppresses its own header on that assumption), and
// the page is added to isExcludedPage below, which is what keeps the level
// switcher and the feed filter bar off it. Whenever the route moves, this
// pattern moves with it.
const PREFERENCES_PATH_RE = /^\/(?:preferences|settings\/location)\/?$/i;
const isPreferencesPath = (pathname) => PREFERENCES_PATH_RE.test(pathname || '');

// Officials pages (the Representatives list plus every individual member,
// senator, president, vice president and justice detail page) live under a
// country prefix, so the country detection above reads them as US pages and
// would otherwise render the Federal / State / Local switcher. None of them has
// a federal-vs-state-vs-local split of its own, so the switcher is suppressed
// on all of them and the section name is rendered as the bar's centered title
// instead, the same way Preferences does it.
//
// Each segment is matched in both singular and plural form, with an optional
// trailing segment for the :bioGuideId / :id parameter, so one pattern covers
// the list route and the detail route:
//   /us/representatives            /us/representative/:bioGuideId
//   /us/senators                   /us/senator/:bioGuideId
//   /us/presidents                 /us/president/:id
//   /us/vicepresidents             /us/vicepresident/:id
//   /us/justices                   /us/justice/:bioGuideId
// The optional trailing segment is what pulls the detail routes in; without it
// the anchored $ stops at the id and the tab bar comes back on detail pages.
// Matched by segment rather than the literal /us path so a future
// /canada/representatives (or any other country) is covered without another
// edit.
//
// Both groups are capturing because getOfficialsTitle below reads them: group 1
// says which section matched, group 2 says whether an id follows. Adding a new
// officials section now means extending OFFICIAL_SEGMENTS *and* adding its
// entry to OFFICIAL_TITLES -- a segment with no entry still suppresses the
// switcher, it just renders no title rather than a wrong one.
const OFFICIAL_SEGMENTS = 'representatives?|senators?|presidents?|vice-?presidents?|justices?';
const OFFICIALS_PATH_RE = new RegExp(`^\\/[^/]+\\/(${OFFICIAL_SEGMENTS})(?:\\/([^/]+))?\\/?$`, 'i');
const isOfficialsPath = (pathname) => OFFICIALS_PATH_RE.test(pathname || '');

// Titles for the officials pages, keyed by the singular of the matched segment.
// Keys are normalized by lowercasing, dropping the hyphen in "vice-president"
// and stripping a trailing "s", so both spellings of every route land on one
// entry.
const OFFICIAL_TITLES = {
  representative: {
    one: ['common:representative', 'Representative'],
    many: ['common:representatives', 'Representatives'],
  },
  senator: {
    one: ['common:senator', 'Senator'],
    many: ['common:senators', 'Senators'],
  },
  president: {
    one: ['common:president', 'President'],
    many: ['common:presidents', 'Presidents'],
  },
  vicepresident: {
    one: ['common:vicePresident', 'Vice President'],
    many: ['common:vicePresidents', 'Vice Presidents'],
  },
  justice: {
    one: ['common:justice', 'Justice'],
    many: ['common:justices', 'Justices'],
  },
};

// Number comes from whether an id is present, NOT from how the segment happens
// to be spelled: an id means one official (singular), no id means the list
// (plural). So /us/representatives/A000370, if such a link ever turns up, still
// titles as "Representative" rather than inheriting the list's plural.
const getOfficialsTitle = (pathname, t) => {
  const match = OFFICIALS_PATH_RE.exec(pathname || '');
  if (!match) return null;
  const key = match[1].toLowerCase().replace(/-/g, '').replace(/s$/, '');
  const entry = OFFICIAL_TITLES[key];
  if (!entry) return null;
  const [translationKey, fallback] = match[2] ? entry.one : entry.many;
  return t(translationKey, fallback);
};

// The two routes FullFeedPage is mounted on, and only those:
//   /feed/:schema/:table
//   /feed/:schema/:table/sort/:sortBy
// NOT /feed/:schema/:table/:id, which is FeedItemDetail. That page has no
// filter bar and still wants the ordinary bar -- country pill, normal row
// spacing -- so a bare startsWith('/feed/') is too wide a net.
//
// The two shapes are told apart by segment count: two segments after /feed is
// the feed, three is an item, four with 'sort' third is the sorted feed. An id
// that happens to be the literal string "sort" reads as an item, which is the
// safe way round.
//
// Note this is deliberately narrower than the /feed branch of isExcludedPage
// below, which suppresses the level switcher on every /feed route including
// detail. Different question, different answer.
const FULL_FEED_PATH_RE = /^\/feed\/[^/]+\/[^/]+(?:\/sort\/[^/]+)?\/?$/i;
const isFullFeedPath = (pathname) => FULL_FEED_PATH_RE.test(pathname || '');

const detectRegionFromPath = (path) => {
  if (path.startsWith('/us/')) {
    const pathSegment = path.split('/')[2];
    if (pathSegment && pathSegment !== 'local') {
      const state = US_STATES_DATA.find(s => s.key === pathSegment || s.path === `/us/${pathSegment}`);
      if (state) return { region: state, country: 'us', level: 'state' };
    }
  }
  if (path.startsWith('/canada/')) {
    const pathSegment = path.split('/')[2];
    if (pathSegment) {
      const province = CANADA_PROVINCES_DATA.find(p => p.key === pathSegment || p.path === `/canada/${pathSegment}`);
      if (province) return { region: province, country: 'canada', level: 'province' };
    }
  }
  if (path.startsWith('/mexico/')) {
    const pathSegment = path.split('/')[2];
    if (pathSegment) {
      const state = MEXICO_STATES_DATA.find(s => s.key === pathSegment || s.path === `/mexico/${pathSegment}`);
      if (state) return { region: state, country: 'mexico', level: 'state' };
    }
  }
  if (path.startsWith('/uk/')) {
    const pathSegment = path.split('/')[2];
    if (pathSegment) {
      const kingdom = UK_KINGDOMS_DATA.find(k => k.key === pathSegment || k.path === `/uk/${pathSegment}`);
      if (kingdom) return { region: kingdom, country: 'uk', level: 'kingdom' };
    }
  }
  return null;
};

const IntegratedSearchNavBar = ({ 
  currentCountry = 'us',
  onCountryChange,
  feedFilterProps,
  reserveNavSpace = true,
  pageTitle = null,
  // Drops the left-hand country / region pill (and, on /us/local, the city
  // report button) from the bar entirely. The pill was costing the feed
  // filters roughly 60px of a row that was already truncating its triggers to
  // "Fe" / "Legisl" on a phone.
  //
  // This is an opt-in for pages outside /feed. Feed routes hide the pill by
  // path regardless of what any caller passes -- see isFeedPath -- so the
  // behaviour doesn't depend on which component happens to mount the bar.
  //
  // Note the pill is the page's ONLY country switcher when the bar is
  // rendering feed filters, since FeedFilterBar is mounted below with
  // hideCountry. A page that hides the pill and still wants a country control
  // has to stop passing hideCountry.
  hideLocationPill = false
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();

  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Measured height of the fixed navbar — drives the spacer so content
  // sits flush regardless of how many rows (level switcher, feed filters) are
  // showing or whether the filter bar wraps to two lines.
  //
  // The ref goes on the Navbar itself, NOT on an inner element. The Navbar is
  // what's position:fixed and therefore what overlays the page, so its height
  // is by definition what content has to clear. Measuring the inner
  // Form.Group instead over-reported: it sits inside a flex Form with
  // align-items-center and carries flex-grow-1 + flex-wrap, so it resolved
  // taller than the box actually painted (151px against a visibly shorter bar).
  const navbarRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);

  const [selectedRegion, setSelectedRegion] = useState(null);
  const [isOnLocalPage, setIsOnLocalPage] = useState(false);

  const [homeCityName, setHomeCityName] = useState(null);
  const [homeCityAbbreviation, setHomeCityAbbreviation] = useState(null);
  const [homeStateInfo, setHomeStateInfo] = useState(null);
  const [homeProvinceInfo, setHomeProvinceInfo] = useState(null);
  const [homeMexicoStateInfo, setHomeMexicoStateInfo] = useState(null);
  const [homeKingdomInfo, setHomeKingdomInfo] = useState(null);

  const [citySearchModalOpen, setCitySearchModalOpen] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [citySearchResults, setCitySearchResults] = useState([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const searchTimeoutRef = useRef(null);

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [selectedReportCity, setSelectedReportCity] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [reportModalView, setReportModalView] = useState('summary');

  useEffect(() => {
    const checkHomeLocation = () => {
      const savedHomeState = localStorage.getItem('homeState');
      const savedHomeCityData = localStorage.getItem('homeCityData');
      if (savedHomeState) { setHomeStateInfo({ key: savedHomeState }); } else { setHomeStateInfo(null); }
      if (savedHomeCityData) {
        try {
          const cityData = JSON.parse(savedHomeCityData);
          setHomeCityName(cityData.name || null);
          if (cityData.name) { setHomeCityAbbreviation(cityData.name.substring(0, 3).toUpperCase()); }
        } catch (e) { setHomeCityName(null); setHomeCityAbbreviation(null); }
      }
      const savedHomeProvince = localStorage.getItem('homeProvince');
      if (savedHomeProvince) {
        try { setHomeProvinceInfo(JSON.parse(savedHomeProvince)); } catch (e) { setHomeProvinceInfo({ key: savedHomeProvince, name: savedHomeProvince }); }
      } else { setHomeProvinceInfo(null); }
      const savedMexicoState = localStorage.getItem('homeMexicanState') || localStorage.getItem('homeMexicoState');
      if (savedMexicoState) {
        const mexicoStateData = MEXICO_STATES_DATA.find(s => s.key === savedMexicoState);
        if (mexicoStateData) { setHomeMexicoStateInfo(mexicoStateData); } else { setHomeMexicoStateInfo({ key: savedMexicoState, name: savedMexicoState }); }
      } else { setHomeMexicoStateInfo(null); }
      const savedHomeKingdom = localStorage.getItem('homeUKRegion') || localStorage.getItem('homeKingdom');
      if (savedHomeKingdom) {
        const kingdomData = UK_KINGDOMS_DATA.find(k => k.key === savedHomeKingdom);
        if (kingdomData) { setHomeKingdomInfo(kingdomData); } else { setHomeKingdomInfo({ key: savedHomeKingdom, name: savedHomeKingdom }); }
      } else { setHomeKingdomInfo(null); }
    };
    checkHomeLocation();
    window.addEventListener('storage', checkHomeLocation);
    return () => window.removeEventListener('storage', checkHomeLocation);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const detected = detectRegionFromPath(path);
    if (detected) { setSelectedRegion(detected.region); setIsOnLocalPage(false); }
    else if (path === '/us/local') { setIsOnLocalPage(true); setSelectedRegion(null); }
    else { setSelectedRegion(null); setIsOnLocalPage(false); }
  }, [location.pathname]);

  const searchCities = useCallback(async (query) => {
    if (!query || query.trim().length < 2) { setCitySearchResults([]); setSearchError(null); setShowCityDropdown(false); return; }
    setSearchingCities(true); setSearchError(null);
    try {
      const response = await fetch(`https://search.govglance.org/api/geocode?search=${encodeURIComponent(query)}`);
      if (!response.ok) throw new Error('Failed to search cities');
      const data = await response.json();
      const validTypes = ['City', 'Town', 'Village', 'Borough', 'Township', 'Municipality', 'Locality'];
      const filteredResults = (data.results || []).filter(result => validTypes.includes(result.type));
      setCitySearchResults(filteredResults);
      setShowCityDropdown(filteredResults.length > 0);
    } catch (error) {
      console.error('City search error:', error);
      setSearchError(t('common:cityError', 'Failed to search cities. Please try again.'));
      setCitySearchResults([]); setShowCityDropdown(false);
    } finally { setSearchingCities(false); }
  }, [t]);

  const handleCitySearchChange = useCallback((e) => {
    const query = e.target.value;
    setCitySearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => { searchCities(query); }, 300);
  }, [searchCities]);

  useEffect(() => { return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); }; }, []);

  const handleCitySelectForReport = useCallback(async (city) => {
    setSelectedReportCity(city); setCitySearchModalOpen(false); setCitySearchQuery('');
    setCitySearchResults([]); setShowCityDropdown(false); setReportModalOpen(true);
    setReportLoading(true); setReportData(null); setReportError(null);
    try {
      const localities = [{ name: city.name, state: city.state, type: city.type || 'City' }];
      const localitiesParam = encodeURIComponent(JSON.stringify(localities));
      const queryParam = encodeURIComponent('government news');
      const url = `https://search.govglance.org/api/public-search?query=${queryParam}&localities=${localitiesParam}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const cleanedContent = data.content ? cleanAISuggestions(data.content) : null;
      setReportData({ city: city.name, state: city.stateName || city.state, type: city.type, content: cleanedContent, citations: data.citations || [], generatedAt: new Date().toLocaleString() });
    } catch (error) { console.error('Error fetching city report:', error); setReportError(t('common:reportError', 'Failed to generate report. Please try again.')); }
    finally { setReportLoading(false); }
  }, [t]);

  const handleCitySearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') { e.preventDefault(); if (citySearchResults.length > 0) handleCitySelectForReport(citySearchResults[0]); }
  }, [citySearchResults, handleCitySelectForReport]);

  const closeCitySearchModal = useCallback(() => { setCitySearchModalOpen(false); setCitySearchQuery(''); setCitySearchResults([]); setShowCityDropdown(false); setSearchError(null); }, []);
  const closeReportModal = useCallback(() => { setReportModalOpen(false); setSelectedReportCity(null); setReportData(null); setReportError(null); setReportModalView('summary'); }, []);

  const styles = `
  #navbarMobileTop { box-shadow: none; width: 100%; border: none; background-color: var(--bg-primary); z-index: 1050; transition: transform 0.3s ease-in-out; overflow: visible; padding: 0; }
  #navbarMobileTop.show { transform: translateY(0); }
  #navbarMobileTop.hide { transform: translateY(-100%); }

  /* The 70px top padding this used to carry unconditionally is status-bar
     clearance for the native Capacitor app. A browser has no status bar to
     clear, and since the spacer below is measured from the real navbar height,
     every one of those pixels showed up as dead space above the page. Web now
     gets ordinary padding; native re-adds the clearance via .is-native. */
     #searchContainer { display: flex; flex-wrap: wrap; width: 100%; padding: 4px 10px; }
  #navbarMobileTop.is-native #searchContainer { padding-top: 70px; }

  /* position: relative is the containing block for the absolutely positioned
     page title (see .page-title-only). #searchContainer pads both sides by the
     same 10px, so the center of this row is the center of the screen.

     --title-inset is how far the title's box stays in from each edge of the
     row. It must clear the WIDER of the two side slots -- the country pill on
     the left is roughly 85px on a phone, the menu button on the right roughly
     50px -- and it is applied to both sides equally so the title stays
     centered on the screen rather than between the two controls. Titles
     longer than the space left ellipsize rather than slide under a control. */
  .search-row { position: relative; --title-inset: 100px; display: flex; width: 100%; align-items: center; flex-wrap: nowrap; gap: 6px; }
  /* Left slot wrapper. It exists so the country pill and the local-page report
     button occupy one shared, measurable box -- on desktop that box is pinned
     to the same width as .search-row-right, which is what keeps the middle
     content optically centered on the window.

     The slot is not rendered at all when hideLocationPill is set, rather than
     rendered empty: an empty slot still claims min-width on desktop, and the
     whole point of hiding the pill is to give that width back to the filter
     bar. Nothing else depends on the two sides being symmetric here, because
     the only thing that needs symmetry -- the centered level switcher -- never
     renders on the pages that hide the pill (/feed is in isExcludedPage). */
  .search-row-left { display: flex; align-items: center; flex-shrink: 0; min-width: 0; }
  .search-row-right { display: flex; align-items: center; gap: 10px; margin-left: auto; flex-shrink: 0; }
  #mobileDropdown { padding-top: 2px; flex-shrink: 0; border-color: transparent; }
  #searchCountry { background-color: var(--search-bg); margin-right: 10px; margin-left: 5px; border-radius: 20px; border: 0; font-size: 16px; color: var(--text-primary); display: flex; align-items: center; justify-content: center; min-width: 44px; height: 36px; padding: 4px 10px; }
  #searchCountry .dropdown-flag-img { height: 22px; width: auto; border-radius: 2px; }
  #searchCountry .city-abbreviation { font-size: 12px; font-weight: 600; letter-spacing: 0.5px; }
  /* Full name beside the flag — desktop only (see .location-name media rule). */
  #searchCountry .location-name { display: none; font-size: 14px; font-weight: 500; line-height: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  #docsDropdown { background-color: var(--dropdown-bg); max-height: 400px; overflow-y: auto; }
  #docsDropdownItem { color: var(--text-primary); cursor: pointer; font-size: 14px; padding: 8px 16px; display: flex; align-items: center; gap: 8px; }
  #docsDropdownItem:hover { background-color: var(--accent-hover); }
  .dropdown-item-separator { border-top: 1px solid var(--border-secondary); margin: 4px 0; }
  .dropdown-region-flag { height: 20px; width: auto; border-radius: 2px; object-fit: contain; }
  .navbar-nav-container { display: flex; align-items: center; gap: 10px; }
  .city-report-btn { background-color: var(--search-bg); margin-right: 10px; margin-left: 5px; border-radius: 20px; border: 0; color: var(--text-primary); display: flex; align-items: center; justify-content: center; min-width: 44px; height: 36px; padding: 4px 10px; cursor: pointer; flex-shrink: 0; }
  .city-report-btn svg { width: 18px; height: 18px; }

  /* Level switcher sits inline in the top row at every width. Search is in
     BottomNavbar on both desktop and mobile, so there's no competing input. */
  .search-row > .level-switcher { display: flex; align-items: center; flex: 1 1 auto; min-width: 0; justify-content: space-evenly; }

  /* Everything in this block is scoped to .is-feed, which the Navbar carries
     only on the two FullFeedPage routes (see pillHidden / FULL_FEED_PATH_RE).
     These rules exist because the country pill is gone there and the filter
     bar is meant to run edge to edge. FeedItemDetail sits under the same
     /feed/ prefix but has no filter bar and keeps the pill, so it must not
     pick these up -- hence the class rather than a path prefix. */

  /* With no left slot the filter bar starts at the container's own padding,
     which reads as flush against the screen edge on a phone. A hair of inset
     keeps it aligned with the cards below it. */
  #navbarMobileTop.is-feed .search-row > .feed-filter-nav-inline:first-child { padding-left: 2px; }

  /* Below 768px the right slot holds exactly one thing: the menu button.
     Donate is display:none there, so it is not a flex item and contributes no
     gap -- but any margin the slot or its wrapper carries lands between the
     sort pill and the menu, which is the gap the filter bar was supposed to
     have absorbed.
     If a gap survives this, it belongs to MobileNavigation's own button
     styles, which are outside this file. */
  @media (max-width: 767px) {
    #navbarMobileTop.is-feed .search-row { gap: 4px; }
    #navbarMobileTop.is-feed .search-row-right { gap: 0; margin-left: 0; }
    #navbarMobileTop.is-feed .search-row-right > .navbar-nav-container { margin: 0; padding: 0; gap: 0; }
    #navbarMobileTop.is-feed .search-row-right .navbar-nav-container > * { margin-right: 0; }
  }

  /* Every interactive state is pinned to the resting appearance. Bootstrap's
     .btn-link ships its own hover / focus / active colors (--bs-btn-hover-color
     and friends) at equal or greater specificity, so pinning only the
     background and box-shadow left the text free to flash Bootstrap's link
     color for the duration of a press. Naming the states explicitly is what
     stops that; the colors carry !important, which means the theme blocks at
     the bottom of this sheet have to match on BOTH specificity and importance
     or they lose to these rules. If you add a state here, add it there too. */
  .level-btn,
  .level-btn.btn-link,
  .level-btn.btn-link:hover,
  .level-btn.btn-link:focus,
  .level-btn.btn-link:focus-visible,
  .level-btn.btn-link:active,
  .level-btn.btn-link:not(:disabled):not(.disabled):active {
    background-color: transparent !important;
    border: none !important;
    border-bottom: 2px solid transparent !important;
    color: var(--text-muted) !important;
    font-size: 14px;
    font-weight: 500;
    padding: 4px 2px;
    border-radius: 0 !important;
    box-shadow: none !important;
    text-decoration: none !important;
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
  }

  /* The last selector outranks the :not(:disabled):not(.disabled):active rule
     above, which would otherwise mute the selected tab while it is held down. */
  .level-btn.active,
  .level-btn.btn-link.active,
  .level-btn.btn-link.active:hover,
  .level-btn.btn-link.active:focus,
  .level-btn.btn-link.active:focus-visible,
  .level-btn.btn-link.active:active,
  .level-btn.btn-link:not(:disabled):not(.disabled).active:active {
    color: var(--text-primary) !important;
    font-weight: 600;
    border-bottom: 2px solid var(--text-primary) !important;
  }

  .level-btn:disabled,
  .level-btn.btn-link:disabled,
  .level-btn.btn-link.disabled { color: var(--text-tertiary) !important; opacity: 0.5; }

  /* Title-only mode: shown on utility pages (Search, Fact Checker, Social,
     Collection, Preferences) and on the officials pages, at every width. Only
     the country dropdown and the offcanvas/menu button remain.

     On a phone the title is taken out of flow and centered on the row itself,
     which is centered on the screen, instead of flexing into the space between
     the two controls. As a flex item it was centered on whatever gap the pill
     and the menu left, and since the pill is wider than the menu, the title sat
     right of the screen's center. Equal insets on both sides (--title-inset,
     set on .search-row) keep it clear of both controls while staying
     symmetric. pointer-events: none so the empty ends of its box never swallow
     a tap aimed near a control.

     Desktop puts it back in flow as the centered middle slot; see the
     title-mode rules in the 768px block. */
  .page-title-only { position: absolute; top: 50%; left: var(--title-inset); right: var(--title-inset); transform: translateY(-50%); font-size: 18px; font-weight: 600; line-height: 1.3; color: var(--text-primary); text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; pointer-events: none; }

  /* Phone: with the title out of flow, the right slot is the only thing after
     the left slot, so it is pushed to the far edge explicitly. Desktop puts
     the title back in flow and zeroes this again, so the two controls sit in
     their slots either side of the title instead of at the window's edges. */
  .search-row.title-mode .search-row-right { margin-left: auto; }

  /* Spacer height is set inline from the measured navbar height (navHeight). */
  .navbar-spacer { width: 100%; flex-shrink: 0; }

  .feed-filter-nav-inline { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; overflow: visible; }
  .feed-filter-nav-inline .feed-filter-bar--inline { flex: 1 1 auto; min-width: 0; }
  .feed-filter-nav-row { width: 100%; padding: 4px 0 0 0; overflow: visible; }
  #navbarMobileTop .navbar-container { overflow: visible; }
  #navbarMobileTop form, #navbarMobileTop .form-group, #searchContainer { overflow: visible !important; }
  .desktop-nav-buttons { display: none; align-items: center; gap: 8px; margin-left: 10px; }

  /* Donate / desktop nav buttons. These are react-bootstrap <Button>s with no
     variant, so Bootstrap renders them as .btn-primary -- and .btn:hover,
     .btn:focus-visible and .btn:active are (0,2,0) selectors that outrank a
     bare .desktop-nav-btn. That is where the primary blue border and focus
     ring were coming from: the resting rule set border/background, then
     Bootstrap re-set border-color and box-shadow the moment the pointer or
     focus landed. Every state is named here, same pattern as .level-btn above,
     with !important on border and box-shadow so nothing Bootstrap ships can
     reintroduce them. Add a state here and mirror it in any theme block. */
  .desktop-nav-btn,
  .desktop-nav-btn:hover,
  .desktop-nav-btn:focus,
  .desktop-nav-btn:focus-visible,
  .desktop-nav-btn:active,
  .desktop-nav-btn:not(:disabled):not(.disabled):active {
    background-color: var(--search-bg) !important;
    border: none !important;
    border-radius: 20px;
    color: var(--text-primary) !important;
    font-size: 13px;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
    box-shadow: none !important;
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
  }
  .desktop-nav-btn:hover,
  .desktop-nav-btn:focus-visible { background-color: var(--search-focus-bg) !important; }
  .desktop-nav-btn .donate-heart { color: #ff0000; }

  .city-search-modal .modal-content { background-color: var(--bg-primary); border: 1px solid var(--border-primary); }
  .city-search-modal-header { background-color: var(--bg-secondary); color: var(--text-primary); border-color: var(--border-primary); }
  .city-search-modal-header h3 { margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px; }
  .city-search-modal-body { padding: 20px; background-color: var(--bg-primary); min-height: 300px; }
  .city-search-input-wrapper { position: relative; margin-bottom: 20px; }
  .city-search-input { width: 100%; padding: 14px 18px; font-size: 15px; background-color: var(--search-bg); color: var(--text-primary); border: 1px solid var(--border-primary); border-radius: 12px; outline: none; }
  .city-search-input::placeholder { color: var(--text-muted); }
  .city-search-input:focus { border-color: var(--accent-primary); background-color: var(--search-focus-bg); }
  .city-search-input.dropdown-open { border-radius: 12px 12px 0 0; }
  .city-search-loading { position: absolute; right: 15px; top: 50%; transform: translateY(-50%); color: var(--text-muted); font-size: 13px; }
  .city-search-dropdown { position: absolute; top: 100%; left: 0; right: 0; max-height: 280px; overflow-y: auto; background-color: var(--dropdown-bg); border: 1px solid var(--border-primary); border-top: none; border-radius: 0 0 12px 12px; z-index: 100; }
  .city-search-dropdown-item { width: 100%; padding: 12px 18px; font-size: 14px; background-color: transparent; color: var(--text-primary); border: none; border-bottom: 1px solid var(--border-secondary); cursor: pointer; text-align: left; display: flex; justify-content: space-between; }
  .city-search-dropdown-item:last-child { border-bottom: none; }
  .city-search-dropdown-item:hover { background-color: var(--accent-hover); }
  .city-search-error { margin-bottom: 15px; padding: 10px; background-color: rgba(220, 53, 69, 0.1); border: 1px solid rgba(220, 53, 69, 0.3); border-radius: 10px; text-align: center; color: #dc3545; font-size: 13px; }
  .city-search-helper { text-align: center; color: var(--text-tertiary); font-size: 13px; }
  .report-modal .modal-content { background-color: var(--bg-primary); border: 1px solid var(--border-primary); }
  .report-modal-header { background-color: var(--bg-secondary); color: var(--text-primary); border-color: var(--border-primary); }
  .report-modal-header h3 { margin: 0; font-size: 18px; display: flex; align-items: center; gap: 8px; }
  .report-modal-body { padding: 20px; background-color: var(--bg-primary); min-height: 400px; }
  .report-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 16px; color: var(--text-secondary); }
  .report-error { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: 16px; color: var(--text-secondary); text-align: center; }
  .report-content { color: var(--text-primary); }
  .report-tab-buttons { display: flex; gap: 10px; margin-bottom: 16px; }
  .report-tab-btn { flex: 1; background-color: var(--bg-card); border: 1px solid var(--border-primary); color: var(--text-primary); padding: 10px 16px; border-radius: 8px; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 6px; }
  .report-tab-btn:hover { background-color: var(--bg-card-hover); }
  .report-tab-btn.active { background-color: var(--accent-primary); border-color: var(--accent-primary); color: white; }
  .report-meta-info { color: var(--text-secondary); margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid var(--border-secondary); }
  .report-markdown-content { line-height: 1.7; font-size: 15px; }
  .report-markdown-content h1, .report-markdown-content h2, .report-markdown-content h3 { margin-top: 20px; margin-bottom: 10px; font-weight: 600; }
  .report-markdown-content h1 { font-size: 22px; } .report-markdown-content h2 { font-size: 18px; } .report-markdown-content h3 { font-size: 16px; }
  .report-markdown-content p { margin-bottom: 12px; }
  .report-markdown-content a { color: var(--accent-primary); text-decoration: none; }
  .report-markdown-content a:hover { text-decoration: underline; }
  .report-no-content { color: var(--text-tertiary); text-align: center; padding: 40px 20px; font-style: italic; }
  .report-citation-card { margin-bottom: 12px; }
  .report-citation-card .card { background-color: var(--bg-card); border: 1px solid var(--border-primary); border-radius: 12px; }
  .report-citation-card .card-body { padding: 16px; }
  .citation-title { font-weight: 500; font-size: 15px; color: var(--text-primary); margin-bottom: 6px; }
  .citation-date { font-size: 13px; color: var(--text-secondary); }
  .report-citation-card .card-footer { background-color: transparent; border-top: 1px solid var(--border-secondary); padding: 12px 16px; }
  @media (min-width: 768px) {
    /* Desktop never carries the native status-bar clearance, in either shell. */
    #searchContainer, #navbarMobileTop.is-native #searchContainer { padding-top: 12px !important; padding-bottom: 8px !important; }
    .desktop-nav-buttons { display: flex; margin-left: 0; }

    /* The switcher is centered on the PAGE, not merely centered inside the
       cluster, and the two sides are symmetric by construction rather than by
       luck. Both slots are pinned to the same width and their contents hug the
       OUTER edge -- the pill at the left edge of the left slot, the Donate /
       menu pair at the right edge of the right slot. Equal slots either side of
       a fixed-width switcher put its center at the window's center, and because
       the contents lean outward, the pill's left edge and the menu's right edge
       land exactly the same distance from that center whatever the country name
       happens to be. Hugging inward instead (the earlier arrangement) pins the
       inner gaps but lets the outer edges drift apart by the difference in the
       two sides' natural widths, which is what read as lopsided.
       Two variables tune this. --nav-switcher-width sets how far Federal and
       Local sit from the centered middle tab. --nav-side-slot sets how far the
       pill and the buttons sit out from the tabs; it must stay at least as wide
       as the widest side or the slot grows and the centering drifts, which is
       why the pill's max-width below reads from the same variable rather than
       carrying its own number.
       This whole scheme only applies when both slots exist. A page that sets
       hideLocationPill renders no left slot, and it also never renders the
       level switcher, so there is nothing to center: the filter bar simply
       takes the width the pill used to hold.

       --title-inset follows --nav-side-slot for the same reason the pill's
       max-width does: both side slots are at most that wide, so a title inset
       by the slot plus the row gap can never run under either one.

       --nav-title-width is the middle slot on the title-mode pages (Search,
       Preferences, Representatives, the Elections and Spending tabs). It is a
       little wider than the switcher's 320px, so the pill and the Donate / menu
       pair sit a touch further out than on the explore pages but stay close to
       the title: 30vw inside a 320px floor and a 440px ceiling, which at a
       1280-1470px window puts the whole group at about 790-830px, centered.
       40vw (the previous value) pushed the controls too far toward the edges.
       Raise the 30vw or the ceiling to spread them, lower them to pull in. */
    .search-row { --nav-switcher-width: 320px; --nav-title-width: clamp(320px, 30vw, 440px); --nav-side-slot: 190px; --title-inset: calc(var(--nav-side-slot) + 12px); max-width: none; margin: 0; gap: 12px; justify-content: center; }
    .search-row-left { flex: 0 0 auto; min-width: var(--nav-side-slot); justify-content: flex-start; }
    .search-row-right { flex: 0 0 auto; min-width: var(--nav-side-slot); margin-left: 0; justify-content: flex-end; }

    /* Fixed width rather than flex-grow, so the tabs spread to a set span
       instead of to whatever the window happens to be. space-between then
       distributes the slack between the tabs. */
    .search-row > .level-switcher { flex: 0 0 var(--nav-switcher-width); max-width: var(--nav-switcher-width); margin: 0; justify-content: space-between; padding: 0 4px; }
    .search-row > .level-switcher .level-btn { font-size: 15px; padding: 4px 10px; }

    /* Feed filters have far more content than three tabs, so they still take
       whatever width is going when they hold the inline spot. */
    .search-row > .feed-filter-nav-inline { flex: 1 1 auto; }

    /* Title mode on desktop uses the same layout as the level switcher: the
       title takes a fixed-width middle slot (--nav-title-width, above), and the
       pill and the Donate / menu pair sit in their equal side slots either side
       of it. The whole group is centered on the page, spread wider than the
       switcher pages but well clear of the window's edges. The phone layout --
       title out of flow, right slot pushed to margin-left: auto -- threw the two
       controls to opposite edges of a wide window. Titles wider than the slot
       still ellipsize. */
    .search-row.title-mode .page-title-only {
      position: static;
      transform: none;
      left: auto;
      right: auto;
      flex: 0 0 var(--nav-title-width);
      max-width: var(--nav-title-width);
    }
    .search-row.title-mode .search-row-right { margin-left: 0; }

    /* Country / region pill: flag plus the full name, sized to its content. The
       cap is --nav-side-slot rather than a number of its own, so a long name
       (e.g. "Newfoundland & Labrador") ellipsizes exactly at the slot edge
       instead of widening the slot and pushing the cluster off center. */
    .search-row-left #searchCountry { justify-content: flex-start; gap: 8px; padding: 4px 14px; max-width: var(--nav-side-slot); margin-left: 0; margin-right: 0; }
    #searchCountry .location-name { display: inline-block; }

    /* Second row (feed filters under the switcher) spans the full width. */
    .feed-filter-nav-row { max-width: none; margin: 0; }
  }
  [data-theme="daylight"] #navbarMobileTop { background-color: #f0f0f0; }
  [data-theme="daylight"] #searchCountry { background-color: #ffffff; color: #1a1a1a; border: 1px solid #e0e0e0; }
  [data-theme="daylight"] #docsDropdown { background-color: #ffffff; border: 1px solid #e0e0e0; }
  [data-theme="daylight"] #docsDropdownItem { color: #1a1a1a; }
  [data-theme="daylight"] #docsDropdownItem:hover { background-color: #fb923c; color: white !important; }

  /* Theme overrides for the level switcher mirror the state list above,
     selector for selector. A shorter list (e.g. just "[data-theme] .level-btn")
     is outranked by the :hover / :active rules in the base block and the tab
     reverts to the var(--text-*) colors mid-press. */
  [data-theme="daylight"] .level-btn,
  [data-theme="daylight"] .level-btn.btn-link,
  [data-theme="daylight"] .level-btn.btn-link:hover,
  [data-theme="daylight"] .level-btn.btn-link:focus,
  [data-theme="daylight"] .level-btn.btn-link:focus-visible,
  [data-theme="daylight"] .level-btn.btn-link:active,
  [data-theme="daylight"] .level-btn.btn-link:not(:disabled):not(.disabled):active { color: #888888 !important; }
  [data-theme="daylight"] .level-btn.active,
  [data-theme="daylight"] .level-btn.btn-link.active,
  [data-theme="daylight"] .level-btn.btn-link.active:hover,
  [data-theme="daylight"] .level-btn.btn-link.active:focus,
  [data-theme="daylight"] .level-btn.btn-link.active:focus-visible,
  [data-theme="daylight"] .level-btn.btn-link.active:active,
  [data-theme="daylight"] .level-btn.btn-link:not(:disabled):not(.disabled).active:active { color: #1a1a1a !important; border-bottom-color: #f97316 !important; }
  [data-theme="daylight"] .level-btn:disabled,
  [data-theme="daylight"] .level-btn.btn-link:disabled { color: #cccccc !important; }

  [data-mode="light"] .level-btn,
  [data-mode="light"] .level-btn.btn-link,
  [data-mode="light"] .level-btn.btn-link:hover,
  [data-mode="light"] .level-btn.btn-link:focus,
  [data-mode="light"] .level-btn.btn-link:focus-visible,
  [data-mode="light"] .level-btn.btn-link:active,
  [data-mode="light"] .level-btn.btn-link:not(:disabled):not(.disabled):active { color: var(--text-tertiary) !important; }
  [data-mode="light"] .level-btn.active,
  [data-mode="light"] .level-btn.btn-link.active,
  [data-mode="light"] .level-btn.btn-link.active:hover,
  [data-mode="light"] .level-btn.btn-link.active:focus,
  [data-mode="light"] .level-btn.btn-link.active:focus-visible,
  [data-mode="light"] .level-btn.btn-link.active:active,
  [data-mode="light"] .level-btn.btn-link:not(:disabled):not(.disabled).active:active { color: var(--text-primary) !important; border-bottom-color: var(--accent-primary) !important; }
  [data-mode="light"] .level-btn:disabled,
  [data-mode="light"] .level-btn.btn-link:disabled { color: var(--text-muted) !important; }

  [data-mode="dark"] .level-btn,
  [data-mode="dark"] .level-btn.btn-link,
  [data-mode="dark"] .level-btn.btn-link:hover,
  [data-mode="dark"] .level-btn.btn-link:focus,
  [data-mode="dark"] .level-btn.btn-link:focus-visible,
  [data-mode="dark"] .level-btn.btn-link:active,
  [data-mode="dark"] .level-btn.btn-link:not(:disabled):not(.disabled):active { color: var(--text-muted) !important; }
  [data-mode="dark"] .level-btn.active,
  [data-mode="dark"] .level-btn.btn-link.active,
  [data-mode="dark"] .level-btn.btn-link.active:hover,
  [data-mode="dark"] .level-btn.btn-link.active:focus,
  [data-mode="dark"] .level-btn.btn-link.active:focus-visible,
  [data-mode="dark"] .level-btn.btn-link.active:active,
  [data-mode="dark"] .level-btn.btn-link:not(:disabled):not(.disabled).active:active { color: var(--text-primary) !important; border-bottom-color: var(--accent-primary) !important; }
  [data-mode="dark"] .level-btn:disabled,
  [data-mode="dark"] .level-btn.btn-link:disabled { color: #505050 !important; }

  /* Daylight's pill styling carries a hairline border, so the desktop nav
     buttons match it here rather than inheriting the borderless dark look.
     Every state is listed for the same reason as the base block. */
  [data-theme="daylight"] .desktop-nav-btn,
  [data-theme="daylight"] .desktop-nav-btn:hover,
  [data-theme="daylight"] .desktop-nav-btn:focus,
  [data-theme="daylight"] .desktop-nav-btn:focus-visible,
  [data-theme="daylight"] .desktop-nav-btn:active,
  [data-theme="daylight"] .desktop-nav-btn:not(:disabled):not(.disabled):active {
    background-color: #ffffff !important;
    color: #1a1a1a !important;
    border: 1px solid #e0e0e0 !important;
    box-shadow: none !important;
  }
  [data-theme="daylight"] .desktop-nav-btn:hover,
  [data-theme="daylight"] .desktop-nav-btn:focus-visible { background-color: #f5f5f5 !important; }

  [data-theme="daylight"] .city-report-btn { background-color: #ffffff; color: #1a1a1a; border: 1px solid #e0e0e0; }
  [data-theme="daylight"] .city-search-dropdown-item:hover { background-color: #fb923c; color: white; }
`;

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);

  // Measure the fixed navbar and keep the spacer in sync. getBoundingClientRect
  // on the Navbar gives exactly the box that overlays the page — no inner-flex
  // guesswork, no padding left out. ResizeObserver catches new rows and the
  // filter bar wrapping to two lines.
  useEffect(() => {
    const el = navbarRef.current;
    if (!el) return;
    const update = () => setNavHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);

  // Local config only used for the 4 core countries + all
  const countryConfig = {
    us: { flag: '🇺🇸', name: 'United States', searchSchema: 'united_states_of_america' },
    canada: { flag: '🇨🇦', name: 'Canada', searchSchema: 'canada' },
    mexico: { flag: '🇲🇽', name: 'Mexico', searchSchema: 'mexico' },
    uk: { flag: '🇬🇧', name: 'United Kingdom', searchSchema: 'united_kingdom' },
    all: { flag: '🌐', name: 'All Countries', searchSchema: 'all_countries' },
  };

  const countryMapping = { 'united_states_of_america': 'us', 'us': 'us', 'united_kingdom': 'uk', 'uk': 'uk', 'canada': 'canada', 'mexico': 'mexico', 'all': 'all', 'ca': 'canada', 'mx': 'mexico' };
  const getCountryConfigKey = (id) => countryMapping[id] || id;

  // US is hardcoded since it's not in countryConfig.js
  // Order: All Countries → US, Canada, Mexico, UK → rest alphabetically from countryConfig.js
  const countryOptions = useMemo(() => {
    const active = getActiveCountries ? getActiveCountries() : [];
    const prioritySlugs = ['us', 'canada', 'mexico', 'uk'];
    const hardcodedPriority = [
      { code: 'us', flag: '🇺🇸', name: 'United States' },
      { code: 'canada', flag: '🇨🇦', name: 'Canada' },
      { code: 'mexico', flag: '🇲🇽', name: 'Mexico' },
      { code: 'uk', flag: '🇬🇧', name: 'United Kingdom' },
    ];
    const others = active
      .filter(c => !prioritySlugs.includes(c.slug))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
      .map(c => ({ code: c.slug, flag: c.flag, name: c.displayName }));
    return [
      { code: 'all', flag: '🌐', name: 'All Countries' },
      ...hardcodedPriority,
      ...others,
    ];
  }, []);

  // Resolution order: URL, then the currentCountry prop, then the last country
  // picked from the dropdown. The URL comes first because the prop defaults to
  // 'us' and so cannot distinguish "this is a US page" from "nothing was
  // passed" -- without the path check, any /us/... page a caller renders
  // without the prop (Representatives, for one) inherits the stored country.
  const getEffectiveCountry = useCallback(() => {
    const fromPath = detectCountryFromPath(location.pathname);
    if (fromPath) {
      return getCountryConfigKey(fromPath);
    }
    if (currentCountry && currentCountry !== 'us') {
      return getCountryConfigKey(currentCountry);
    }
    const stored = localStorage.getItem('selectedCountry') || localStorage.getItem('homeCountry');
    if (stored) { return getCountryConfigKey(stored); }
    return 'us';
  }, [currentCountry, location.pathname]);

  const effectiveCountry = getEffectiveCountry();

  // Resolve flag/name: use local countryConfig for core 4, fall back to countryOptions for others
  const countryFromOptions = countryOptions.find(c => c.code === effectiveCountry);
  const currentCountryInfo = countryConfig[effectiveCountry] ||
    (countryFromOptions
      ? { flag: countryFromOptions.flag, name: countryFromOptions.name, searchSchema: effectiveCountry }
      : { flag: '🌐', name: effectiveCountry, searchSchema: effectiveCountry });

  // Drives the .is-native class on the navbar, which is what re-adds the 70px
  // status-bar clearance. Web builds stay at the ordinary 8px.
  const isNativeApp = Capacitor.isNativePlatform();
  const showUSLevelSwitcher = effectiveCountry === 'us';
  const showCanadaLevelSwitcher = effectiveCountry === 'ca' || effectiveCountry === 'canada';
  const showMexicoLevelSwitcher = effectiveCountry === 'mx' || effectiveCountry === 'mexico';
  const showUKLevelSwitcher = effectiveCountry === 'uk';
  // Countries without region data: show a Federal-only switcher (Federal active,
  // secondary level disabled).
  const showNewCountryLevelSwitcher = NEW_COUNTRY_SLUGS.includes(effectiveCountry);

  const onPreferencesPage = isPreferencesPath(location.pathname);

  // Null on every non-officials route, so it simply drops out of the navTitle
  // chain below. See getOfficialsTitle for how the label is picked.
  const officialsTitle = useMemo(
    () => getOfficialsTitle(location.pathname, t),
    [location.pathname, t]
  );

  // Prop OR path. The path half is what makes this hold no matter which
  // component renders the bar on a FullFeedPage route.
  //
  // It also gates the .is-feed class on the Navbar, which every layout
  // override in the styles block above is scoped to. Those overrides assume no
  // left slot and a filter bar running edge to edge — both true only on the
  // full feed. FeedItemDetail (/feed/:schema/:table/:id), home pages and
  // everything else keep the pill, the centered level switcher and the
  // symmetric side slots exactly as they were.
  const pillHidden = hideLocationPill || isFullFeedPath(location.pathname);

  // Utility pages and officials pages render a title in the bar instead of the
  // explore controls (country dropdown, level switcher, feed filters). The
  // title is derived from the path so the page itself doesn't have to pass
  // anything; a pageTitle prop, if supplied, takes precedence.
  //
  // /us/elections-dashboard and /us/spending-dashboard are the Elections and
  // Spending tabs' own pages, titled like Representatives. Not /us/elections
  // and /us/spending, which are Federal feed topics and keep the explore
  // controls.
  const pathTitleMap = {
    '/social': t('common:social', 'Social'),
    '/fact-check': t('common:factChecker', 'Gove Glance Fact Checker'),
    '/collection': t('common:collection', 'Collection'),
    '/us/elections-dashboard': t('common:elections', 'Elections'),
    '/us/spending-dashboard': t('common:spending', 'Spending'),
    '/us/spending-dashboard/awards': t('common:newAwards', 'New awards'),
  };
  const isSearchPage = location.pathname === '/search' || location.pathname.startsWith('/search/');
  const navTitle =
    pageTitle ||
    pathTitleMap[location.pathname] ||
    (onPreferencesPage ? t('common:preferences', 'Preferences') : null) ||
    officialsTitle ||
    (isSearchPage ? t('common:search', 'Search') : null);

  // The centered page title used to be suppressed on desktop because the bar
  // carried a search input there. It no longer does, so the title now applies
  // at every width.
  const titleMode = !!navTitle;

  // Pages where the level switcher should never appear: feed, search, the
  // officials pages (Representatives list plus every member / senator /
  // president / vice president / justice detail page), and the title-mode
  // utility pages above.
  const isExcludedPage = useMemo(() => {
    const path = location.pathname;
    if (path === '/search' || path.startsWith('/search/')) return true;   // /search, /search/:country
    if (path === '/feed' || path.startsWith('/feed/')) return true;       // all /feed/... variants
    if (path === '/social' || path === '/fact-check' || path === '/collection') return true;
    if (path === '/us/elections-dashboard' || path.startsWith('/us/spending-dashboard')) return true; // Elections / Spending tab pages, and the full awards feed
    if (isOfficialsPath(path)) return true;                               // /us/representatives, /us/senator/:bioGuideId, etc.
    if (isPreferencesPath(path)) return true;                             // /preferences (and the former /settings/location)
    return false;
  }, [location.pathname]);

  const showLevelSwitcher = !isExcludedPage && (showUSLevelSwitcher || showCanadaLevelSwitcher || showMexicoLevelSwitcher || showUKLevelSwitcher || showNewCountryLevelSwitcher);

  // currentName is the full label shown beside the flag on desktop (the CSS
  // hides it below 768px, where the pill stays flag-only to leave room for the
  // inline level switcher / feed filters).
  const getDropdownOptions = useMemo(() => {
    if (isOnLocalPage && effectiveCountry === 'us') {
      return { type: 'local', options: [], currentDisplay: homeCityAbbreviation || 'LOC', currentName: homeCityName, displayType: 'text' };
    }
    if (selectedRegion) {
      if (effectiveCountry === 'us') return { type: 'us-state', options: US_STATES_DATA, currentDisplay: selectedRegion.flag, currentName: selectedRegion.name, displayType: 'image' };
      if (effectiveCountry === 'canada') return { type: 'ca-province', options: CANADA_PROVINCES_DATA, currentDisplay: selectedRegion.flag, currentName: selectedRegion.name, displayType: 'image' };
      if (effectiveCountry === 'mexico') return { type: 'mx-state', options: MEXICO_STATES_DATA, currentDisplay: selectedRegion.flag, currentName: selectedRegion.name, displayType: 'image' };
      if (effectiveCountry === 'uk') return { type: 'uk-kingdom', options: UK_KINGDOMS_DATA, currentDisplay: selectedRegion.flag, currentName: selectedRegion.name, displayType: 'image' };
    }
    return { type: 'country', options: countryOptions, currentDisplay: currentCountryInfo.flag, currentName: currentCountryInfo.name, displayType: 'emoji' };
  }, [effectiveCountry, countryOptions, currentCountryInfo, selectedRegion, homeCityAbbreviation, homeCityName, isOnLocalPage]);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    if (currentScrollY > lastScrollY && currentScrollY > 100) setShowNavbar(false);
    else if (currentScrollY < lastScrollY) setShowNavbar(true);
    setLastScrollY(currentScrollY);
  }, [lastScrollY]);

  useEffect(() => {
    let timeoutId = null;
    const throttled = () => { if (!timeoutId) timeoutId = setTimeout(() => { handleScroll(); timeoutId = null; }, 10); };
    window.addEventListener('scroll', throttled, { passive: true });
    return () => { window.removeEventListener('scroll', throttled); if (timeoutId) clearTimeout(timeoutId); };
  }, [handleScroll]);

  const handleHapticsClick = useCallback(async (cb) => {
    try { await Haptics.impact({ style: ImpactStyle.Light }); } catch {}
    cb();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleCountryChange = useCallback((slug) => {
    localStorage.setItem('selectedCountry', slug);
    if (onCountryChange) onCountryChange(slug);
    else navigate(slug === 'us' ? '/us' : `/${slug}`);
  }, [navigate, onCountryChange]);

  const handleRegionChange = useCallback((region) => {
    setSelectedRegion(region);
    navigate(region.path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  // Every navigate() here passes LEVEL_SWITCH_NAV_STATE so the resulting history
  // entry is marked as a tab switch. MobileBackButton hides itself on those
  // entries -- Federal / State / Province / Kingdom / Local are siblings, so
  // there is no parent to go back to. The flag lives on the history entry, so
  // it survives leaving the page and coming back to it.
  const handleLevelChange = useCallback(async (level) => {
    try { await Haptics.impact({ style: ImpactStyle.Light }); } catch {}
    if (level === 'federal') {
      let path = '/us';
      if (effectiveCountry === 'canada') path = '/canada';
      else if (effectiveCountry === 'mexico') path = '/mexico';
      else if (effectiveCountry === 'uk') path = '/uk';
      else if (NEW_COUNTRY_SLUGS.includes(effectiveCountry)) path = `/${effectiveCountry}`;
      navigate(path, LEVEL_SWITCH_NAV_STATE);
    } else if (level === 'state') {
      if (effectiveCountry === 'us') { if (selectedRegion) navigate(selectedRegion.path, LEVEL_SWITCH_NAV_STATE); else if (homeStateInfo) navigate(`/us/${homeStateInfo.key}`, LEVEL_SWITCH_NAV_STATE); }
      else if (effectiveCountry === 'mexico') { if (selectedRegion) navigate(selectedRegion.path, LEVEL_SWITCH_NAV_STATE); else if (homeMexicoStateInfo) navigate(`/mexico/${homeMexicoStateInfo.key}`, LEVEL_SWITCH_NAV_STATE); }
    } else if (level === 'province') {
      if (selectedRegion) navigate(selectedRegion.path, LEVEL_SWITCH_NAV_STATE); else if (homeProvinceInfo) navigate(`/canada/${homeProvinceInfo.key}`, LEVEL_SWITCH_NAV_STATE);
    } else if (level === 'kingdom') {
      if (selectedRegion) navigate(selectedRegion.path, LEVEL_SWITCH_NAV_STATE); else if (homeKingdomInfo) navigate(`/uk/${homeKingdomInfo.key}`, LEVEL_SWITCH_NAV_STATE);
    } else if (level === 'local') {
      navigate('/us/local', LEVEL_SWITCH_NAV_STATE);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [effectiveCountry, selectedRegion, homeStateInfo, homeProvinceInfo, homeMexicoStateInfo, homeKingdomInfo, navigate]);

  const handleDesktopNavClick = useCallback((path, external = false) => {
    if (external) window.open(path, '_blank');
    else { navigate(path); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  }, [navigate]);

  // react-bootstrap's Button already renders type="button", so these tabs never
  // submitted the surrounding Form. Any plain <button> added to this bar must
  // set type="button" itself -- see the report button below.
  const renderLevelSwitcher = () => {
    if (showUSLevelSwitcher) {
      return (
        <div className="level-switcher">
          <Button variant="link" className={`level-btn ${!selectedRegion && !isOnLocalPage ? 'active' : ''}`} onClick={() => handleLevelChange('federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className={`level-btn ${selectedRegion && effectiveCountry === 'us' ? 'active' : ''}`} onClick={() => handleLevelChange('state')} disabled={!homeStateInfo && !selectedRegion}>{t('common:levelState', 'State')}</Button>
          <Button variant="link" className={`level-btn ${isOnLocalPage ? 'active' : ''}`} onClick={() => handleLevelChange('local')} >{t('common:levelLocal', 'Local')}</Button>
        </div>
      );
    }
    if (showCanadaLevelSwitcher) {
      return (
        <div className="level-switcher">
          <Button variant="link" className={`level-btn ${!selectedRegion ? 'active' : ''}`} onClick={() => handleLevelChange('federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className={`level-btn ${selectedRegion ? 'active' : ''}`} onClick={() => handleLevelChange('province')} disabled={!homeProvinceInfo && !selectedRegion}>{t('common:levelProvince', 'Province')}</Button>
        </div>
      );
    }
    if (showMexicoLevelSwitcher) {
      return (
        <div className="level-switcher">
          <Button variant="link" className={`level-btn ${!selectedRegion ? 'active' : ''}`} onClick={() => handleLevelChange('federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className={`level-btn ${selectedRegion ? 'active' : ''}`} onClick={() => handleLevelChange('state')} disabled={!homeMexicoStateInfo && !selectedRegion}>{t('common:levelState', 'State')}</Button>
        </div>
      );
    }
    if (showUKLevelSwitcher) {
      return (
        <div className="level-switcher">
          <Button variant="link" className={`level-btn ${!selectedRegion ? 'active' : ''}`} onClick={() => handleLevelChange('federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className={`level-btn ${selectedRegion ? 'active' : ''}`} onClick={() => handleLevelChange('kingdom')} disabled={!homeKingdomInfo && !selectedRegion}>{t('common:levelKingdom', 'Kingdom')}</Button>
        </div>
      );
    }
    if (showNewCountryLevelSwitcher) {
      // No region data for these countries yet: Federal is always the active
      // tab, and the secondary level is shown disabled (not selectable).
      return (
        <div className="level-switcher">
          <Button variant="link" className="level-btn active" onClick={() => handleLevelChange('federal')}>{t('common:levelFederal', 'Federal')}</Button>
          <Button variant="link" className="level-btn" disabled>{t('common:levelState', 'State')}</Button>
        </div>
      );
    }
    return null;
  };

  // The name span is always rendered; CSS shows it only at >=768px so the pill
  // stays a flag-sized square on mobile where the row is already crowded.
  const renderDropdownToggle = () => {
    const { currentDisplay, currentName, displayType } = getDropdownOptions;
    const nameLabel = currentName ? <span className="location-name">{currentName}</span> : null;
    if (displayType === 'emoji') return <>{currentDisplay}{nameLabel}</>;
    if (displayType === 'image') return <><Image src={currentDisplay} alt="Flag" className="dropdown-flag-img" />{nameLabel}</>;
    if (displayType === 'text') return <><span className="city-abbreviation">{currentDisplay}</span>{nameLabel}</>;
    return currentDisplay;
  };

  const renderDropdownItems = () => {
    const { type, options } = getDropdownOptions;
    if (type === 'local') return <Dropdown.Item id="docsDropdownItem" disabled>{homeCityName || t('common:noCitySelected', 'No city selected')}</Dropdown.Item>;
    if (type === 'country') {
      return options.map((c, i) => (
        <React.Fragment key={c.code}>
          {i === 1 && <Dropdown.Divider className="dropdown-item-separator" />}
          <Dropdown.Item id="docsDropdownItem" onClick={() => handleHapticsClick(() => handleCountryChange(c.code))}>{c.flag} {c.name}</Dropdown.Item>
        </React.Fragment>
      ));
    }
    return options.map((r) => (
      <Dropdown.Item key={r.key} id="docsDropdownItem" onClick={() => handleHapticsClick(() => handleRegionChange(r))}>
        <Image src={r.flag} alt={`${r.name} flag`} className="dropdown-region-flag" /><span>{r.name}</span>
      </Dropdown.Item>
    ));
  };

  const renderLeftElement = () => {
    if (isOnLocalPage && effectiveCountry === 'us') {
      // type="button" is load-bearing. This is a plain <button>, and the whole
      // bar is wrapped in a <Form>, so without it the element defaults to
      // type="submit": clicking it submitted the form, the browser did a full
      // page load, and the component remounted with citySearchModalOpen back at
      // false -- the modal appeared to never open at all.
      return (
        <button
          type="button"
          className="city-report-btn"
          onClick={() => setCitySearchModalOpen(true)}
          title={t('common:generateCityReport', 'Generate city report')}
        >
          <Newspaper />
        </button>
      );
    }
    return (
      <Dropdown id="mobileDropdown" align="start">
        <Dropdown.Toggle id="searchCountry">{renderDropdownToggle()}</Dropdown.Toggle>
        <Dropdown.Menu id="docsDropdown">{renderDropdownItems()}</Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <>
      <Navbar
        id="navbarMobileTop"
        ref={navbarRef}
        fixed="top"
        className={`navbar-container ${showNavbar ? 'show' : 'hide'}${isNativeApp ? ' is-native' : ''}${pillHidden ? ' is-feed' : ''}`}
      >
        {/* The Form is a layout wrapper, not a real form -- nothing here is
            meant to submit. The guard stops any future control from triggering
            a page reload the way the report button used to. */}
        <Form className="text-center d-flex align-items-center w-100" onSubmit={(e) => e.preventDefault()}>
          <Form.Group id="searchContainer" className="d-flex flex-grow-1">
            {/* One row at every width: country/region pill, then either the page
                title or the level switcher / feed filters, then Donate (desktop
                only, via CSS) and the menu. The left and right slots are equal
                widths on desktop, which is what centers the middle content.

                In title mode the row carries .title-mode. On a phone the title
                is absolutely positioned on the row's center (the screen's
                center); on desktop it takes the switcher's middle slot, so the
                pill and the menu sit in the same places as on the explore
                pages.

                The left slot is omitted entirely when hideLocationPill is set,
                so its width goes to whatever sits inline. */}
            <div className={`search-row${titleMode ? ' title-mode' : ''}`}>
              {!pillHidden && (
                <div className="search-row-left">{renderLeftElement()}</div>
              )}

              {titleMode ? (
                <div className="page-title-only">{navTitle}</div>
              ) : (
                <>
                  {showLevelSwitcher && renderLevelSwitcher()}
                  {feedFilterProps && !showLevelSwitcher && (
                    <div className="feed-filter-nav-inline">
                      <FeedFilterBar {...feedFilterProps} hideCountry inline />
                    </div>
                  )}
                </>
              )}

              <div className="search-row-right">
                <div className="desktop-nav-buttons">
                  <Button className="desktop-nav-btn" onClick={() => handleDesktopNavClick('https://donate.stripe.com/28o3fXbdvbQO2RO288', true)}>
                    <HeartFill className="donate-heart" /><span>{t('common:donate', 'Donate')}</span>
                  </Button>
                </div>
                <div className="navbar-nav-container"><MobileNavigation currentCountry={effectiveCountry} /></div>
              </div>
            </div>

            {/* Feed filters drop to their own row only when the level switcher
                is taking the inline slot. */}
            {!titleMode && feedFilterProps && showLevelSwitcher && (
              <div className="feed-filter-nav-row">
                <FeedFilterBar {...feedFilterProps} hideCountry inline />
              </div>
            )}
          </Form.Group>
        </Form>
      </Navbar>
      <div className="navbar-spacer" style={{ height: reserveNavSpace ? navHeight : 0 }} />

      <Modal show={citySearchModalOpen} onHide={closeCitySearchModal} size="lg" centered className="city-search-modal">
        <Modal.Header className="city-search-modal-header" closeButton><h3><Building /> {t('common:findACity', 'Find a City')}</h3></Modal.Header>
        <Modal.Body className="city-search-modal-body">
          <div className="city-search-input-wrapper">
            <input type="text" value={citySearchQuery} onChange={handleCitySearchChange} onKeyDown={handleCitySearchKeyDown} onFocus={() => citySearchResults.length > 0 && setShowCityDropdown(true)} placeholder={t('common:typeCityName', 'Type a city name...')} className={`city-search-input ${showCityDropdown ? 'dropdown-open' : ''}`} autoFocus />
            {searchingCities && <div className="city-search-loading">{t('common:searching', 'Searching...')}</div>}
            {showCityDropdown && !searchingCities && citySearchResults.length > 0 && (
              <div className="city-search-dropdown">
                {citySearchResults.map((city, i) => (
                  <button type="button" key={`${city.name}_${city.state}_${i}`} onClick={() => handleCitySelectForReport(city)} className="city-search-dropdown-item">
                    <span><span style={{ fontWeight: "500" }}>{city.name}</span><span style={{ opacity: 0.7 }}>, {city.stateName}</span></span>
                    <span style={{ fontSize: "12px", opacity: 0.5 }}>{city.type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          {searchError && <div className="city-search-error">{searchError}</div>}
          <div className="city-search-helper">
            {citySearchQuery.length < 2 ? <p>{t('common:startTypingCity', 'Start typing to search for a city')}</p> : !searchingCities && citySearchResults.length === 0 && citySearchQuery.length >= 2 ? <p>{t('common:cityNoResults', 'No cities found matching "{{query}}"', { query: citySearchQuery })}</p> : null}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={reportModalOpen} onHide={closeReportModal} size="xl" centered className="report-modal" scrollable>
        <Modal.Header className="report-modal-header" closeButton><h3><Newspaper /> {t('common:cityReport', '{{city}} Report', { city: selectedReportCity?.name })}</h3></Modal.Header>
        <Modal.Body className="report-modal-body">
          {reportLoading ? (
            <div className="report-loading"><Spinner animation="border" variant="primary" /><span>{t('common:generatingReport', 'Generating report for {{city}}...', { city: selectedReportCity?.name })}</span></div>
          ) : reportError ? (
            <div className="report-error"><p>{reportError}</p><Button variant="primary" onClick={() => selectedReportCity && handleCitySelectForReport(selectedReportCity)}>{t('common:tryAgain', 'Try Again')}</Button></div>
          ) : reportData ? (
            <div className="report-content">
              <div className="report-tab-buttons">
                <Button className={`report-tab-btn ${reportModalView === 'summary' ? 'active' : ''}`} onClick={() => setReportModalView('summary')}><FileText className="me-1" /> {t('common:report', 'Report')}</Button>
                <Button className={`report-tab-btn ${reportModalView === 'sources' ? 'active' : ''}`} onClick={() => setReportModalView('sources')}><List className="me-1" /> {t('common:sourcesCount', 'Sources ({{count}})', { count: reportData.citations?.length || 0 })}</Button>
              </div>
              <div className="report-meta-info"><small>{reportData.generatedAt}<span> • {reportData.city}, {reportData.state}</span></small></div>
              {reportModalView === 'summary' && (reportData.content ? <ReportContent content={reportData.content} citations={reportData.citations} /> : <p className="report-no-content">{t('common:noSummaryAvailable', 'No summary available.')}</p>)}
              {reportModalView === 'sources' && (reportData.citations?.length > 0 ? reportData.citations.map((c, i) => (
                <div key={`citation-${c.url}-${i}`} className="report-citation-card">
                  <Card>
                    <Card.Body onClick={() => openCapacitorSite(c.url, '_blank')} style={{ cursor: 'pointer' }}>
                      <div className="citation-title">{c.title}</div>
                      {c.date && <div className="citation-date">{new Date(c.date).toLocaleDateString()}</div>}
                    </Card.Body>
                    <Card.Footer className="d-flex"><Button className="clickable me-2" id="billLink" onClick={() => openCapacitorSite(c.url, '_blank')}><InfoCircle /> {t('common:source', 'Source')}</Button></Card.Footer>
                  </Card>
                </div>
              )) : <p className="report-no-content">{t('common:noSourcesAvailable', 'No sources available.')}</p>)}
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default IntegratedSearchNavBar;